import {
  interpolatePlayheadContentX,
  type SongPlaybackTimeline,
} from "./songPlayback";

/** Columna base de la aguja (antes del lead-in) respecto al ancho útil del carril. */
export const PLAYHEAD_BASE_OFFSET_PX = 56;

/**
 * Fracción del ancho del viewport: la aguja puede desplazarse a la derecha hasta
 * `base + width * leadIn` sin desplazar el scroll; después el scroll absorbe el avance.
 * 0.25 = empiece a desplazarse el papel al llegar al **primer cuarto** de la pantalla.
 */
export const PLAYHEAD_SCROLL_LEAD_IN_FRAC = 0.25;

/**
 * Ajuste fino del scroll (px): valores **negativos** desplazan menos el contenido
 * y acercan la primera figura/silencio bajo la aguja (la geometría a veces marca el
 * borde izquierdo del glifo).
 */
export const PLAYHEAD_SCROLL_ALIGN_NUDGE_PX = -2;

/**
 * Durante el primer evento de la línea de tiempo (hasta el siguiente ataque), resta este
 * valor del `scrollLeft` deseado para mostrar cejilla / inicio del mástil a la izquierda
 * de la aguja. La columna roja sigue alineada al contenido vía `playheadLeftPx`.
 */
export const PLAYBACK_VIEWPORT_START_MARGIN_PX = 120;

/** Suavizado del margen al acabar el 1.er evento del timeline (evita tirón de scroll). */
export const VIEWPORT_START_MARGIN_BLEND_OUT_MS = 240;

export function blendedOpeningViewportMarginPx(
  songMs: number,
  firstSegmentEndMs: number | undefined,
  fullMargin: number,
  blendOutMs: number = VIEWPORT_START_MARGIN_BLEND_OUT_MS
): number {
  if (fullMargin <= 0) return 0;
  if (firstSegmentEndMs === undefined || !Number.isFinite(firstSegmentEndMs) || firstSegmentEndMs <= 1e-6) {
    return 0;
  }
  if (songMs >= firstSegmentEndMs) return 0;
  const t0 = Math.max(0, firstSegmentEndMs - blendOutMs);
  if (songMs <= t0) return fullMargin;
  const u = (firstSegmentEndMs - songMs) / blendOutMs;
  return fullMargin * Math.max(0, Math.min(1, u));
}

/**
 * Aplica el tope de scroll del contenedor y recalcula la aguja en coordenadas
 * visibles. Cuando `maxScrollLeft === 0` (partitura completa a la vista) o al
 * final del carril, la aguja sigue hasta la última figura en vez de quedarse
 * anclada a la columna de lead-in.
 */
export function resolvePlaybackScrollAgainstContent(
  contentX: number,
  wantScrollLeft: number,
  maxScrollLeft: number
): { scrollLeft: number; playheadLeftPx: number } {
  const scrollLeft = Math.min(Math.max(0, wantScrollLeft), Math.max(0, maxScrollLeft));
  return {
    scrollLeft,
    playheadLeftPx: contentX - scrollLeft,
  };
}

/**
 * Pentagrama + mástil: mismo criterio que `layoutPlaybackLanesAndPlayhead`, con margen
 * opcional de apertura (`startMarginPx`). Mientras el avance del contenido respecto al
 * inicio no supere ~un cuarto del viewport, el scroll no avanza y la aguja se desliza
 * hacia la derecha dentro de esa franja.
 */
export function layoutStaffHighwayScrollForPlayback(
  xStaff: number,
  xStaffAtSongStart: number,
  xHighway: number,
  xHighwayAtSongStart: number,
  viewportWidth: number,
  startMarginPx: number = 0
): { staffScrollLeft: number; highwayScrollLeft: number; playheadLeftPx: number } {
  const P = PLAYHEAD_BASE_OFFSET_PX;
  const nudge = PLAYHEAD_SCROLL_ALIGN_NUDGE_PX;
  const L = Math.max(24, viewportWidth * PLAYHEAD_SCROLL_LEAD_IN_FRAC);
  const excess = xStaff - xStaffAtSongStart;
  const m = startMarginPx;

  if (excess <= L) {
    const staffScrollLeft = Math.max(0, xStaffAtSongStart - P + nudge - m);
    const highwayScrollLeft = Math.max(0, xHighwayAtSongStart - P + nudge - m);
    return {
      staffScrollLeft,
      highwayScrollLeft,
      playheadLeftPx: xStaff - staffScrollLeft,
    };
  }

  const staffScrollLeft = Math.max(0, xStaff - P - L + nudge - m);
  const highwayScrollLeft = Math.max(0, xHighway - P - L + nudge - m);
  return {
    staffScrollLeft,
    highwayScrollLeft,
    playheadLeftPx: xStaff - staffScrollLeft,
  };
}

/**
 * Vista en reposo al abrir la canción: borde izquierdo del pentagrama visible (clave, compás)
 * sin forzar scroll hacia el primer evento del timeline ni margen de apertura artificial.
 */
export function layoutIdleScoreOpening(contentStartPx: number): PlaybackLaneScrollLayout {
  const P = PLAYHEAD_BASE_OFFSET_PX;
  const playhead = Math.max(P, contentStartPx);
  return {
    staffScrollLeft: 0,
    highwayScrollLeft: 0,
    playheadLeftPx: playhead,
  };
}

export interface PlaybackLaneScrollLayout {
  staffScrollLeft: number;
  highwayScrollLeft: number;
  /** Posición CSS `left` del overlay de playhead (columna del needle). */
  playheadLeftPx: number;
}

/**
 * Calcula scroll de pentagrama y mástil + posición horizontal del playhead,
 * con fase inicial de “lectura” sin desplazar el papel.
 */
export function layoutPlaybackLanesAndPlayhead(
  playbackMs: number,
  viewportWidth: number,
  timeline: SongPlaybackTimeline | null,
  staffXAtTime: (divisionTime: number) => number,
  highwayXAtTime: (divisionTime: number) => number
): PlaybackLaneScrollLayout {
  const P = PLAYHEAD_BASE_OFFSET_PX;
  const nudge = PLAYHEAD_SCROLL_ALIGN_NUDGE_PX;

  if (!timeline || viewportWidth <= 1) {
    const s = timeline
      ? interpolatePlayheadContentX(timeline, playbackMs, staffXAtTime)
      : 0;
    const h = timeline
      ? interpolatePlayheadContentX(timeline, playbackMs, highwayXAtTime)
      : 0;
    return {
      staffScrollLeft: Math.max(0, s - P + nudge),
      highwayScrollLeft: Math.max(0, h - P + nudge),
      playheadLeftPx: P,
    };
  }

  const s0 = interpolatePlayheadContentX(timeline, 0, staffXAtTime);
  const h0 = interpolatePlayheadContentX(timeline, 0, highwayXAtTime);
  const s = interpolatePlayheadContentX(timeline, playbackMs, staffXAtTime);
  const h = interpolatePlayheadContentX(timeline, playbackMs, highwayXAtTime);
  return layoutStaffHighwayScrollForPlayback(s, s0, h, h0, viewportWidth, 0);
}

/** Pulso de entrada en unidades <divisions> (alineado con getCountInPulseMs). */
export function countInSubdivisionDivisions(divisions: number, beatType: number): number {
  return beatType === 8 ? Math.max(1e-9, divisions / 2) : Math.max(1e-9, divisions);
}

/** Pulsos de entrada en anacrusa: hueco del compás incompleto y/o tiempo hasta el primer ataque no silencio. */
export function computePickupPulseCount(
  m0: { startTime: number; length: number; contentLength: number; isPickup: boolean } | undefined,
  divisions: number,
  beatType: number,
  firstAttackDiv: number
): number {
  if (!m0?.isPickup || divisions <= 0) return 0;
  const sub = countInSubdivisionDivisions(divisions, beatType);
  const padDiv = Math.max(0, m0.length - m0.contentLength);
  const padPulses = Math.round(padDiv / sub);
  const attackPulses =
    firstAttackDiv > m0.startTime + 1e-9 ? Math.round((firstAttackDiv - m0.startTime) / sub) : 0;
  const n = Math.max(padPulses, attackPulses);
  return n > 0 ? n : 0;
}

/**
 * Durante anacrusa antes del primer ataque: aguja y scroll avanzan **a saltos** por cada
 * subdivisión de entrada (corchea en 6/8, negra en 4/4), alineados con la cuenta y el pentagrama.
 */
export function layoutPlaybackPickupIntro(
  playbackMs: number,
  viewportWidth: number,
  params: {
    /** Fin de la fase de entrada (ms), p. ej. `max(firstAttackStartMs, pickupPulseCount * pulseMs)`. */
    introEndMs: number;
    m0StartDiv: number;
    m0LengthDiv: number;
    pickupPulseCount: number;
    divisions: number;
    beatType: number;
    pulseMs: number;
    staffXAtTime: (divisionTime: number) => number;
    highwayXAtTime: (divisionTime: number) => number;
  }
): PlaybackLaneScrollLayout | null {
  if (playbackMs < 0 || playbackMs >= params.introEndMs - 1e-6 || params.pulseMs <= 0 || params.introEndMs <= 1e-6) {
    return null;
  }

  const subDiv = countInSubdivisionDivisions(params.divisions, params.beatType);
  const totalSteps = Math.max(1, params.pickupPulseCount);
  const stepIdx = Math.min(totalSteps - 1, Math.max(0, Math.floor(playbackMs / params.pulseMs)));
  const div = Math.min(
    params.m0StartDiv + params.m0LengthDiv - 1e-4,
    params.m0StartDiv + subDiv * (stepIdx + 1)
  );

  const s0 = params.staffXAtTime(params.m0StartDiv);
  const h0 = params.highwayXAtTime(params.m0StartDiv);
  const s = params.staffXAtTime(div);
  const h = params.highwayXAtTime(div);

  return layoutStaffHighwayScrollForPlayback(s, s0, h, h0, viewportWidth, 0);
}
